import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { incomeBasis } from "@/lib/incomePlanner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Loader2, Wand2, ArrowRight } from "lucide-react";

// AI Optimizer card — Claude proposes new weights on existing tickers,
// shows a before/after diff, and hands the proposed weights back to the
// PortfolioBuilder via `onApply` when the user accepts.
export function OptimizerCard({ form, onApply, initialSuggestion, seedVersion, onSuggestionChange }) {
  const [goal, setGoal] = useState(initialSuggestion?.goal || "balanced");
  const [busy, setBusy] = useState(false);
  const [suggestion, setSuggestionState] = useState(initialSuggestion || null); // {rationale, current, proposed}
  const [err, setErr] = useState("");
  const nav = useNavigate();
  const { user } = useAuth();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setSuggestionState(initialSuggestion || null); }, [seedVersion]);
  const setSuggestion = (s) => { setSuggestionState(s); onSuggestionChange?.(s); };

  const monthly = (summary) => incomeBasis(summary).monthly;

  const run = async () => {
    setBusy(true); setErr(""); setSuggestionState(null);
    try {
      const res = await api.post("/portfolios/optimize", {
        holdings: form.holdings.map((h) => ({ ticker: h.ticker, weight: Number(h.weight) })),
        initial_investment: Number(form.initial_investment),
        start_date: form.start_date,
        end_date: form.end_date,
        drip: !!form.drip,
        additional_contribution: Number(form.additional_contribution) || 0,
        contribution_frequency: form.contribution_frequency || "monthly",
        goal,
      });
      setSuggestion({ ...res.data, goal });
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e.message || "Optimizer failed";
      setErr(detail);
      if (status === 402) {
        if (!user) {
          toast.error(detail, {
            action: {
              label: "Create free account",
              onClick: () => nav(`/auth?mode=register&next=${encodeURIComponent(window.location.pathname + window.location.search)}&intent=ai-quota`),
            },
            duration: 10000,
          });
        } else {
          toast.error(detail, {
            action: { label: "Upgrade", onClick: () => nav("/pricing") },
            duration: 8000,
          });
        }
      } else {
        toast.error(detail);
      }
    } finally {
      setBusy(false);
    }
  };

  const apply = () => {
    if (!suggestion?.proposed?.weights) return;
    onApply?.(suggestion);
    setSuggestion(null);
  };

  return (
    <Card className="border-secondary/30 bg-secondary/[0.03]">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <div className="mono text-[10px] uppercase tracking-widest text-secondary flex items-center gap-2">
              <Wand2 className="w-3.5 h-3.5" /> AI Optimizer
            </div>
            <h3 className="serif text-2xl mt-2">Rebalance for your goal</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Claude proposes new weights on your existing tickers (DRIP stays {form.drip ? "on" : "off"}).
              No new tickers added or removed.
            </p>
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <Label htmlFor="opt-goal" className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Optimize for</Label>
              <select
                id="opt-goal"
                data-testid="optimizer-goal"
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                className="mt-1 block rounded-md border border-border bg-background px-3 py-2 text-sm mono focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <option value="yield">Max yield-on-cost (income)</option>
                <option value="cagr">Max CAGR (growth)</option>
                <option value="balanced">Balanced</option>
              </select>
            </div>
            <Button
              data-testid="optimizer-run"
              onClick={run}
              disabled={busy || form.holdings.length < 2}
              className="rounded-full"
            >
              {busy
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Optimizing…</>
                : <><Wand2 className="w-4 h-4 mr-2" /> Suggest new weights</>}
            </Button>
          </div>
        </div>

        {err && !suggestion && (
          <div className="text-xs text-destructive" data-testid="optimizer-error">{err}</div>
        )}

        {suggestion && (
          <div data-testid="optimizer-suggestion" className="space-y-4">
            <div className="rounded-md border border-secondary/30 bg-secondary/[0.06] px-4 py-3">
              <div className="mono text-[10px] uppercase tracking-widest text-secondary">Why this rebalance</div>
              <p className="text-sm mt-1 text-foreground/85">{suggestion.rationale || "Weights adjusted to fit the selected goal."}</p>
            </div>

            {/* Before / After metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <BeforeAfter
                label="Yield on cost"
                before={`${Number(suggestion.current.summary.yield_on_cost_pct).toFixed(2)}%`}
                after={`${Number(suggestion.proposed.summary.yield_on_cost_pct).toFixed(2)}%`}
                delta={Number(suggestion.proposed.summary.yield_on_cost_pct) - Number(suggestion.current.summary.yield_on_cost_pct)}
                fmtDelta={(d) => `${d >= 0 ? "+" : ""}${d.toFixed(2)}pp`}
              />
              <BeforeAfter
                label="CAGR"
                before={`${Number(suggestion.current.summary.cagr_pct).toFixed(2)}%`}
                after={`${Number(suggestion.proposed.summary.cagr_pct).toFixed(2)}%`}
                delta={Number(suggestion.proposed.summary.cagr_pct) - Number(suggestion.current.summary.cagr_pct)}
                fmtDelta={(d) => `${d >= 0 ? "+" : ""}${d.toFixed(2)}pp`}
              />
              <BeforeAfter
                label="Est. monthly income"
                before={`$${monthly(suggestion.current.summary).toFixed(2)}`}
                after={`$${monthly(suggestion.proposed.summary).toFixed(2)}`}
                delta={monthly(suggestion.proposed.summary) - monthly(suggestion.current.summary)}
                fmtDelta={(d) => `${d >= 0 ? "+" : "−"}$${Math.abs(d).toFixed(2)}/mo`}
              />
            </div>

            {/* Weight diff table */}
            <div className="overflow-hidden rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/60">
                  <tr className="text-left">
                    <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground">Ticker</th>
                    <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Current</th>
                    <th className="px-3 py-2"></th>
                    <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Proposed</th>
                    <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Δ</th>
                  </tr>
                </thead>
                <tbody data-testid="optimizer-weights">
                  {suggestion.proposed.weights.map((row) => {
                    const cur = suggestion.current.weights.find((w) => w.ticker === row.ticker);
                    const delta = Number(row.weight) - Number(cur?.weight || 0);
                    return (
                      <tr key={row.ticker} className="border-t border-border">
                        <td className="px-3 py-1.5 mono font-semibold">{row.ticker}</td>
                        <td className="px-3 py-1.5 mono text-right">{Number(cur?.weight || 0).toFixed(2)}%</td>
                        <td className="px-3 py-1.5 text-center text-muted-foreground"><ArrowRight className="w-3.5 h-3.5 inline" /></td>
                        <td className="px-3 py-1.5 mono text-right text-primary">{Number(row.weight).toFixed(2)}%</td>
                        <td className={`px-3 py-1.5 mono text-right ${delta >= 0 ? "text-primary" : "text-destructive"}`}>
                          {delta >= 0 ? "+" : ""}{delta.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setSuggestion(null)} data-testid="optimizer-dismiss">Discard</Button>
              <Button onClick={apply} className="rounded-full" data-testid="optimizer-apply">
                Apply weights <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function BeforeAfter({ label, before, after, delta, fmtDelta }) {
  const positive = Number(delta) >= 0;
  return (
    <div className="rounded-md border border-border bg-card px-4 py-3">
      <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="flex items-baseline justify-between mt-1.5 gap-2">
        <div className="mono text-sm text-muted-foreground line-through">{before}</div>
        <ArrowRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        <div className="serif text-xl text-primary">{after}</div>
      </div>
      <div className={`mono text-[11px] mt-1 ${positive ? "text-primary" : "text-destructive"}`}>
        {fmtDelta(Number(delta))}
      </div>
    </div>
  );
}
